import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ScrollText, Search, Loader2, Monitor, Smartphone,
  ChevronLeft, ChevronRight, X
} from "lucide-react";

const AdminOfferAuditLog = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [actionFilter, setActionFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [jobIdFilter, setJobIdFilter] = useState("");
  const limit = 20;

  useEffect(() => {
    const isAdmin = localStorage.getItem("shootzu_admin");
    const token = localStorage.getItem("shootzu_admin_token");
    if (!isAdmin || !token) navigate("/szadmin");
  }, [navigate]);

  useEffect(() => {
    const jid = searchParams.get("job_id");
    if (jid) setJobIdFilter(jid);
  }, [searchParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, actionFilter, sourceFilter, jobIdFilter]);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, searchQuery, actionFilter, sourceFilter, jobIdFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const API = process.env.REACT_APP_BACKEND_URL || "https://shootzu.com";
      const token = localStorage.getItem("shootzu_admin_token");
      const res = await axios.get(`${API}/api/admin/offer-activity-log`, {
        params: {
          page: currentPage,
          limit,
          search: searchQuery || undefined,
          action: actionFilter || undefined,
          source: sourceFilter || undefined,
          job_id: jobIdFilter || undefined,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data.logs || []);
      setTotalLogs(res.data.total || 0);
      setHasMore(res.data.has_more || false);
    } catch (err) {
      console.error("Error fetching offer audit log:", err);
      if (err.response?.status === 401) {
        navigate("/szadmin");
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalLogs / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const formatJobId = (id) => {
    if (!id) return "N/A";
    return `JOB-${id.substring(0, 6).toUpperCase()}`;
  };

  const handleJobFilter = (jobId) => {
    setJobIdFilter(jobId);
    setCurrentPage(1);
  };

  const ActionBadge = ({ action }) => {
    const accepted = action === "accepted";
    const declined = action === "declined";
    let fg = "#475569";
    let bg = "#E2E8F0";
    let label = action ? action.toUpperCase() : "—";
    if (accepted) { fg = "#15803D"; bg = "#DCFCE7"; label = "ACCEPTED"; }
    if (declined) { fg = "#B91C1C"; bg = "#FEE2E2"; label = "DECLINED"; }
    return (
      <span
        className="inline-flex items-center"
        style={{ fontFamily: "Poppins", fontSize: 10.5, fontWeight: 700, color: fg, background: bg, padding: "4px 9px", borderRadius: 999 }}
      >
        {label}
      </span>
    );
  };

  const SourceCell = ({ source }) => {
    const isMobile = source === "mobile_app";
    const Icon = isMobile ? Smartphone : Monitor;
    const label = isMobile ? "Mobile App" : "Email Link";
    return (
      <div className="flex items-center gap-1.5" style={{ fontSize: 11.5, color: "#475569" }}>
        <Icon className="w-[13px] h-[13px]" />
        {label}
      </div>
    );
  };

  // Filter pill button styles per prototype (active = Ink fill)
  const pillStyle = (active) => ({
    padding: "7px 12px",
    border: `1px solid ${active ? "#0C1120" : "#E0E3EB"}`,
    borderRadius: 999,
    background: active ? "#0C1120" : "#fff",
    color: active ? "#fff" : "#2B3142",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  });

  const FilterPill = ({ label, active, onClick }) => (
    <button onClick={onClick} style={pillStyle(active)}>
      {label}
    </button>
  );

  // Numbered pagination window (max 4 buttons, per prototype)
  const buildPageNums = () => {
    const win = Math.min(4, totalPages);
    const p0 = Math.max(1, Math.min(currentPage - 1, totalPages - win + 1));
    const nums = [];
    for (let i = 0; i < win; i++) nums.push(p0 + i);
    return nums;
  };

  const rangeStart = totalLogs === 0 ? 0 : (currentPage - 1) * limit + 1;
  const rangeEnd = Math.min(currentPage * limit, totalLogs);

  return (
    <div>
      {/* Sticky Header */}
      <header className="sz-header">
        <h1 className="sz-h1 m-0">Offer Audit Log</h1>
        <div className="mt-[3px]" style={{ fontSize: 12.5, color: "#8A90A2" }}>
          {totalLogs} offer responses logged
        </div>
      </header>

      <main style={{ padding: "22px 30px 56px", maxWidth: 1400 }}>
        {/* Filter bar */}
        <div className="sz-card flex items-center gap-[14px] flex-wrap" style={{ padding: "14px 16px" }}>
          <div className="relative flex-1" style={{ minWidth: 220 }}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#A8AEBC" }} />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search email, offer ID or job ID…"
              className="sz-input"
              style={{ paddingLeft: 36 }}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="sz-overline" style={{ color: "#8A90A2" }}>Action</span>
            <FilterPill label="All" active={!actionFilter} onClick={() => setActionFilter("")} />
            <FilterPill label="Accepted" active={actionFilter === "accepted"} onClick={() => setActionFilter("accepted")} />
            <FilterPill label="Declined" active={actionFilter === "declined"} onClick={() => setActionFilter("declined")} />
          </div>

          <div className="flex items-center gap-2">
            <span className="sz-overline" style={{ color: "#8A90A2" }}>Source</span>
            <FilterPill label="All" active={!sourceFilter} onClick={() => setSourceFilter("")} />
            <FilterPill label="Email Link" active={sourceFilter === "email_link"} onClick={() => setSourceFilter("email_link")} />
            <FilterPill label="Mobile App" active={sourceFilter === "mobile_app"} onClick={() => setSourceFilter("mobile_app")} />
          </div>
        </div>

        {jobIdFilter && (
          <div className="flex items-center gap-2 mt-3">
            <span
              className="inline-flex items-center gap-1.5"
              style={{ fontSize: 12, fontWeight: 600, color: "#2B3142", background: "#fff", border: "1px solid #E0E3EB", padding: "5px 11px", borderRadius: 999 }}
            >
              Filtered by Job: {formatJobId(jobIdFilter)}
              <button onClick={() => setJobIdFilter("")} className="hover:text-red-500" style={{ color: "#8A90A2" }}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#572B7C" }} />
          </div>
        ) : logs.length === 0 ? (
          <div className="sz-card text-center mt-4" style={{ padding: "48px 24px", color: "#8A90A2" }}>
            <ScrollText className="w-12 h-12 mx-auto mb-3" style={{ color: "#A8AEBC" }} />
            <p className="font-semibold" style={{ color: "#2B3142" }}>No audit records match your filters.</p>
            <p className="text-sm mt-1">Offer accept/decline actions will appear here</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block sz-card mt-4 overflow-hidden">
              <div
                className="grid items-center gap-3 sz-overline"
                style={{
                  gridTemplateColumns: "150px 110px 100px 1.4fr 1.4fr 110px 110px",
                  padding: "13px 18px",
                  borderBottom: "1px solid #EEF0F4",
                  background: "#FAFBFC",
                  color: "#8A90A2",
                }}
              >
                <div>Timestamp</div>
                <div>Job ID</div>
                <div>Action</div>
                <div>Crew Email</div>
                <div>Owner Email</div>
                <div>Source</div>
                <div>IP</div>
              </div>

              {logs.map((log, idx) => (
                <div
                  key={log.id || idx}
                  className="grid items-center gap-3 hover:bg-[#FAFBFC]"
                  style={{
                    gridTemplateColumns: "150px 110px 100px 1.4fr 1.4fr 110px 110px",
                    padding: "11px 18px",
                    borderBottom: "1px solid #F1F3F7",
                  }}
                >
                  <div style={{ fontFamily: "Poppins", fontSize: 12, color: "#2B3142" }}>
                    {formatDate(log.timestamp)}
                    <div style={{ fontSize: 11, color: "#8A90A2" }}>{formatTime(log.timestamp)}</div>
                  </div>
                  <div>
                    <button
                      onClick={() => handleJobFilter(log.job_id)}
                      style={{ fontFamily: "Poppins", fontSize: 11.5, color: "#2563EB", fontWeight: 600, cursor: "pointer" }}
                      className="hover:underline"
                    >
                      {formatJobId(log.job_id)}
                    </button>
                  </div>
                  <div><ActionBadge action={log.action} /></div>
                  <div className="truncate" style={{ fontSize: 12, color: "#2B3142" }}>{log.crew_email || "N/A"}</div>
                  <div className="truncate" style={{ fontSize: 12, color: "#2B3142" }}>{log.owner_email || "N/A"}</div>
                  <div><SourceCell source={log.source} /></div>
                  <div style={{ fontFamily: "Poppins", fontSize: 11.5, color: "#8A90A2" }}>{log.ip || "N/A"}</div>
                </div>
              ))}
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col gap-[10px] mt-4">
              {logs.map((log, idx) => (
                <div key={log.id || idx} className="sz-card" style={{ padding: 13, borderRadius: 13 }}>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleJobFilter(log.job_id)}
                      style={{ fontFamily: "Poppins", fontSize: 11.5, color: "#2563EB", fontWeight: 600, cursor: "pointer" }}
                    >
                      {formatJobId(log.job_id)}
                    </button>
                    <ActionBadge action={log.action} />
                  </div>
                  <div className="mt-2" style={{ fontSize: 12, color: "#2B3142" }}>{log.crew_email || "N/A"}</div>
                  <div className="mt-0.5" style={{ fontSize: 12, color: "#2B3142" }}>{log.owner_email || "N/A"}</div>
                  <div className="mt-0.5" style={{ fontSize: 11.5, color: "#8A90A2" }}>
                    {formatDate(log.timestamp)} {formatTime(log.timestamp)} · {log.source === "mobile_app" ? "Mobile App" : "Email Link"} · {log.ip || "N/A"}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between gap-3 mt-[18px] flex-wrap">
              <span style={{ fontSize: 12.5, color: "#8A90A2" }}>
                {totalLogs === 0 ? "No results" : `Showing ${rangeStart}–${rangeEnd} of ${totalLogs}`}
              </span>
              {totalPages > 1 && (
                <div className="flex gap-[5px]">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center"
                    style={{ minWidth: 34, height: 34, border: "1px solid #E0E3EB", borderRadius: 9, background: "#fff", color: "#2B3142", cursor: currentPage === 1 ? "not-allowed" : "pointer", opacity: currentPage === 1 ? 0.5 : 1 }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {buildPageNums().map((pageNum) => {
                    const active = currentPage === pageNum;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        style={{
                          minWidth: 34,
                          height: 34,
                          border: `1px solid ${active ? "#0C1120" : "#E0E3EB"}`,
                          borderRadius: 9,
                          background: active ? "#0C1120" : "#fff",
                          color: active ? "#fff" : "#2B3142",
                          fontFamily: "Poppins",
                          fontSize: 12.5,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center"
                    style={{ minWidth: 34, height: 34, border: "1px solid #E0E3EB", borderRadius: 9, background: "#fff", color: "#2B3142", cursor: currentPage === totalPages ? "not-allowed" : "pointer", opacity: currentPage === totalPages ? 0.5 : 1 }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminOfferAuditLog;
